import cx from 'classnames';
import Image from 'next/image';
import { MouseEvent, useCallback } from 'react';

import { ampli } from '../../../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../../../core/Tracking/Amplitude/Amplitude';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import { imageLoaderForId } from '../../../../../domains/Layout/Image/URL/ImageURL';
import { Destination } from '../../../../../resources/destination/domain/types/destination';
import { useUrlGenerator } from '../../../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../../../shared/utilities/types/PickDomain';

import style from './DestinationCard.module.css';

type Props = {
    variant?: 'fluid' | 'carousel';
    destination: PickDomain<Destination, 'id' | 'name' | 'slug' | 'coverPicture'>;
    trackingInformation: {
        click: TrackingProperties<typeof ampli.destinationClicked>;
    };
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

const PICTURE_WIDTH = 450;
const PICTURE_HEIGHT = 356;

export function DestinationCard({
    variant = 'fluid',
    destination,
    onClick,
    trackingInformation,
}: Props) {
    const { trackEvent } = useTrackEventAmplitude();
    const { generatePath } = useUrlGenerator();

    const destinationUrl = generatePath('destination', {
        destinationSlug: destination.slug,
    });

    const handleClick = useCallback(
        (event: MouseEvent<HTMLAnchorElement>) => {
            if (typeof onClick === 'function') {
                onClick(event);
            }

            trackEvent((ampli, defaultProperties) => {
                ampli.destinationClicked({
                    ...defaultProperties,
                    ...trackingInformation.click,
                });
            });
        },
        [onClick, trackEvent, trackingInformation],
    );

    return (
        <div
            className={cx(style.card, {
                [style.cardFluid]: variant === 'fluid',
                [style.cardCarousel]: variant === 'carousel',
            })}
        >
            <div className={style.pictureWrapper}>
                <Image
                    fill
                    alt={destination.name}
                    className={style.picture}
                    sizes={`${PICTURE_WIDTH}px`}
                    src={destination.coverPicture.id.toString()}
                    loader={imageLoaderForId({
                        aspectRatio: PICTURE_WIDTH / PICTURE_HEIGHT,
                    })}
                />
            </div>
            <Typography
                as="a"
                href={destinationUrl}
                scale="secondary-xs-bold"
                className={style.destinationName}
                onClick={handleClick}
            >
                {destination.name}
            </Typography>
        </div>
    );
}
